import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { Mail, RefreshCw, Plus, CheckCircle, AlertCircle, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

interface MailConfig {
    provider: string;
    smtpHost: string | null;
    smtpPort: number;
    smtpUseStartTls: boolean;
    smtpUsername: string | null;
    smtpPasswordSet: boolean;
    zeptoApiKeySet: boolean;
    zeptoBaseUrl: string;
    zeptoAccountApiKeySet: boolean;
    zeptoDomainKeys: Record<string, boolean> | null;
    zeptoAutoProvision: boolean;
    sendGridApiKeySet: boolean;
    defaultFromEmail: string;
    defaultFromName: string;
    updatedAt: string;
    updatedBy: string | null;
}

interface ZeptoDnsRecord {
    type: string;
    host: string;
    value: string;
    purpose: string;
}

interface ZeptoDomain {
    domainKey: string;
    domain: string;
    status: string;
    sendTokenAvailable: boolean;
    dnsRecords: ZeptoDnsRecord[];
}

const PROVIDERS = ["zeptomail", "smtp", "postfix", "sendgrid", "log"] as const;
type Provider = (typeof PROVIDERS)[number];

export function MailConfigPage() {
    const { token } = useAuth();

    const [config, setConfig] = useState<MailConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<{ type: "ok" | "err"; msg: string } | null>(null);

    const [provider, setProvider] = useState<Provider>("zeptomail");
    const [smtpHost, setSmtpHost] = useState("");
    const [smtpPort, setSmtpPort] = useState(10025);
    const [smtpTls, setSmtpTls] = useState(false);
    const [smtpUser, setSmtpUser] = useState("");
    const [smtpPass, setSmtpPass] = useState("");
    const [showSmtpPass, setShowSmtpPass] = useState(false);
    const [zeptoKey, setZeptoKey] = useState("");
    const [showZeptoKey, setShowZeptoKey] = useState(false);
    const [zeptoAccountKey, setZeptoAccountKey] = useState("");
    const [showZeptoAccountKey, setShowZeptoAccountKey] = useState(false);
    const [zeptoBase, setZeptoBase] = useState("https://api.zeptomail.in/");
    const [sendgridKey, setSendgridKey] = useState("");
    const [showSendgridKey, setShowSendgridKey] = useState(false);
    const [fromEmail, setFromEmail] = useState("noreply@webmail.athavita.com");
    const [fromName, setFromName] = useState("Athavita Mail");

    const [zeptoAutoProvision, setZeptoAutoProvision] = useState(false);
    const [domainKeys, setDomainKeys] = useState<Record<string, string>>({});
    const [newDomain, setNewDomain] = useState("");
    const [newDomainKey, setNewDomainKey] = useState("");

    const [zeptoDomains, setZeptoDomains] = useState<ZeptoDomain[]>([]);
    const [newZeptoDomain, setNewZeptoDomain] = useState("");
    const [creatingDomain, setCreatingDomain] = useState(false);
    const [expandedDomain, setExpandedDomain] = useState<string | null>(null);

    const authHeader = { Authorization: `Bearer ${token ?? ""}` };

    async function loadConfig() {
        setLoading(true);
        try {
            const r = await fetch(`${API_BASE}/api/system/mail/config`, { headers: authHeader });
            if (r.ok) {
                const c: MailConfig = await r.json();
                setConfig(c);
                setProvider((c.provider as Provider) ?? "zeptomail");
                setSmtpHost(c.smtpHost ?? "");
                setSmtpPort(c.smtpPort);
                setSmtpTls(c.smtpUseStartTls);
                setSmtpUser(c.smtpUsername ?? "");
                setZeptoBase(c.zeptoBaseUrl);
                setZeptoAutoProvision(c.zeptoAutoProvision ?? false);
                setFromEmail(c.defaultFromEmail);
                setFromName(c.defaultFromName);
                const existing: Record<string, string> = {};
                for (const d of Object.keys(c.zeptoDomainKeys ?? {})) {
                    existing[d] = "";
                }
                setDomainKeys(existing);
            }
        } finally { setLoading(false); }
    }

    async function loadZeptoDomains() {
        try {
            const r = await fetch(`${API_BASE}/api/system/mail/zepto-domains`, { headers: authHeader });
            if (r.ok) setZeptoDomains(await r.json());
        } catch { /* non-critical */ }
    }

    useEffect(() => { void loadConfig(); }, []);
    useEffect(() => {
        if (provider === "zeptomail") void loadZeptoDomains();
    }, [provider]);

    async function save() {
        setSaving(true);
        try {
            const loadedDomains = new Set(Object.keys(config?.zeptoDomainKeys ?? {}));
            const delta: Record<string, string> = {};
            let hasDelta = false;
            for (const d of loadedDomains) {
                if (!(d in domainKeys)) { delta[d] = ""; hasDelta = true; }
            }
            for (const [d, k] of Object.entries(domainKeys)) {
                if (k.trim()) { delta[d] = k.trim(); hasDelta = true; }
            }

            const payload: Record<string, unknown> = {
                provider,
                smtpHost: smtpHost || null,
                smtpPort,
                smtpUseStartTls: smtpTls,
                smtpUsername: smtpUser || null,
                smtpPassword: smtpPass || null,
                zeptoApiKey: zeptoKey || null,
                zeptoBaseUrl: zeptoBase,
                zeptoAccountApiKey: zeptoAccountKey || null,
                zeptoDomainKeys: hasDelta ? delta : null,
                zeptoAutoProvision,
                sendGridApiKey: sendgridKey || null,
                defaultFromEmail: fromEmail,
                defaultFromName: fromName,
            };
            const r = await fetch(`${API_BASE}/api/system/mail/config`, {
                method: "PUT",
                headers: { ...authHeader, "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (r.ok) {
                const updated: MailConfig = await r.json();
                setConfig(updated);
                showToast("ok", "Mail provider config saved. Changes take effect within 2 minutes.");
                setSmtpPass(""); setZeptoKey(""); setZeptoAccountKey(""); setSendgridKey("");
            } else {
                const err = await r.json().catch(() => ({}));
                showToast("err", (err as { detail?: string }).detail ?? `Save failed (${r.status})`);
            }
        } finally { setSaving(false); }
    }

    async function createZeptoDomain() {
        if (!newZeptoDomain.trim()) return;
        setCreatingDomain(true);
        try {
            const r = await fetch(`${API_BASE}/api/system/mail/zepto-domains`, {
                method: "POST",
                headers: { ...authHeader, "Content-Type": "application/json" },
                body: JSON.stringify({ domain: newZeptoDomain.trim() }),
            });
            if (r.ok) {
                const d: ZeptoDomain = await r.json();
                setZeptoDomains(prev => [...prev.filter(x => x.domainKey !== d.domainKey), d]);
                setExpandedDomain(d.domainKey);
                setNewZeptoDomain("");
                showToast("ok", `Domain ${d.domain} created. Add the DNS records below, then verify.`);
            } else {
                const err = await r.json().catch(() => ({}));
                showToast("err", (err as { detail?: string }).detail ?? `Failed (${r.status})`);
            }
        } finally { setCreatingDomain(false); }
    }

    async function verifyZeptoDomain(domainKey: string) {
        const r = await fetch(`${API_BASE}/api/system/mail/zepto-domains/${encodeURIComponent(domainKey)}/verify`, {
            method: "POST",
            headers: authHeader,
        });
        if (r.ok) showToast("ok", "Verification requested. ZeptoMail will check DNS within a few minutes.");
        else {
            const err = await r.json().catch(() => ({}));
            showToast("err", (err as { detail?: string }).detail ?? `Verify failed (${r.status})`);
        }
    }

    function addDomainKey() {
        if (!newDomain.trim() || !newDomainKey.trim()) return;
        setDomainKeys(prev => ({ ...prev, [newDomain.trim().toLowerCase()]: newDomainKey.trim() }));
        setNewDomain(""); setNewDomainKey("");
    }

    function showToast(type: "ok" | "err", msg: string) {
        setToast({ type, msg });
        setTimeout(() => setToast(null), 5000);
    }

    const isSmtp = provider === "smtp" || provider === "postfix";
    const isZepto = provider === "zeptomail";
    const isSendGrid = provider === "sendgrid";

    if (loading) return <div className="text-sm text-muted-foreground p-4">Loading mail config…</div>;

    return (
        <div className="max-w-3xl space-y-6">
            <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                <h1 className="text-xl font-semibold">Mail provider</h1>
            </div>

            {toast && (
                <div className={`flex items-center gap-2 rounded-md border px-4 py-3 text-sm ${
                    toast.type === "ok"
                        ? "border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200"
                        : "border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
                }`}>
                    {toast.type === "ok"
                        ? <CheckCircle className="h-4 w-4 shrink-0" />
                        : <AlertCircle className="h-4 w-4 shrink-0" />}
                    {toast.msg}
                </div>
            )}

            {config?.updatedBy && (
                <p className="text-xs text-muted-foreground">
                    Last updated {new Date(config.updatedAt).toLocaleString()} by {config.updatedBy}
                </p>
            )}

            <Section title="Provider">
                <div className="flex flex-wrap gap-2">
                    {PROVIDERS.map(p => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => setProvider(p)}
                            className={`rounded-md border px-4 py-1.5 text-sm font-medium transition-colors ${
                                provider === p
                                    ? "border-primary bg-primary/10 text-primary"
                                    : "border-border hover:bg-accent"
                            }`}
                        >
                            {p}
                        </button>
                    ))}
                </div>
                {provider === "log" && (
                    <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                        Log provider accepts every send and logs it. For dev/test only — no real email is delivered.
                    </p>
                )}
            </Section>

            <Section title="Default sender">
                <Row label="From address">
                    <Input value={fromEmail} onChange={e => setFromEmail(e.target.value)} placeholder="noreply@webmail.athavita.com" />
                </Row>
                <Row label="Display name">
                    <Input value={fromName} onChange={e => setFromName(e.target.value)} placeholder="Athavita Mail" />
                </Row>
            </Section>

            {isSmtp && (
                <Section title="SMTP settings">
                    <Row label="Host">
                        <Input value={smtpHost} onChange={e => setSmtpHost(e.target.value)} placeholder="127.0.0.1" />
                    </Row>
                    <Row label="Port">
                        <Input type="number" value={smtpPort} onChange={e => setSmtpPort(Number(e.target.value))} />
                    </Row>
                    <Row label="STARTTLS">
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={smtpTls} onChange={e => setSmtpTls(e.target.checked)} />
                            Enable STARTTLS
                        </label>
                    </Row>
                    <Row label="Username">
                        <Input value={smtpUser} onChange={e => setSmtpUser(e.target.value)} placeholder="(leave blank for no auth)" />
                    </Row>
                    <Row label="Password">
                        <div className="flex gap-2">
                            <Input
                                type={showSmtpPass ? "text" : "password"}
                                value={smtpPass}
                                onChange={e => setSmtpPass(e.target.value)}
                                placeholder={config?.smtpPasswordSet ? "•••• (set — enter new to change)" : "(none)"}
                            />
                            <button type="button" onClick={() => setShowSmtpPass(v => !v)}
                                className="p-2 text-muted-foreground hover:text-foreground">
                                {showSmtpPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </Row>
                </Section>
            )}

            {isZepto && (
                <>
                    <Section title="ZeptoMail — sending">
                        <Row label="API endpoint">
                            <Input value={zeptoBase} onChange={e => setZeptoBase(e.target.value)} />
                        </Row>
                        <Row label="Default Send Token">
                            <div className="flex gap-2">
                                <Input
                                    type={showZeptoKey ? "text" : "password"}
                                    value={zeptoKey}
                                    onChange={e => setZeptoKey(e.target.value)}
                                    placeholder={config?.zeptoApiKeySet ? "•••• (set — enter new to change)" : "Zoho-enczapikey …"}
                                />
                                <button type="button" onClick={() => setShowZeptoKey(v => !v)}
                                    className="p-2 text-muted-foreground hover:text-foreground">
                                    {showZeptoKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">Per-Mail-Agent send token (default when no domain-specific token matches).</p>
                        </Row>

                        <p className="text-sm font-medium mt-4 mb-1">Per-domain Send Tokens</p>
                        <p className="text-xs text-muted-foreground mb-3">
                            ZeptoMail issues one token per verified Mail Agent. Add a token here for each sending domain.
                        </p>
                        {Object.entries(domainKeys).map(([d]) => (
                            <div key={d} className="flex items-center gap-2 mb-2">
                                <span className="w-40 shrink-0 text-sm font-mono">{d}</span>
                                <span className="text-sm text-muted-foreground">
                                    {config?.zeptoDomainKeys?.[d] ? "token set" : "new"}
                                </span>
                                <button type="button" className="ml-auto text-xs text-destructive hover:underline"
                                    onClick={() => setDomainKeys(prev => { const n = { ...prev }; delete n[d]; return n; })}>
                                    Remove
                                </button>
                            </div>
                        ))}
                        <div className="flex gap-2 mt-2">
                            <Input placeholder="domain (e.g. dairygiant.com)" value={newDomain} onChange={e => setNewDomain(e.target.value)} className="w-48" />
                            <Input placeholder="Zoho-enczapikey …" type="password" value={newDomainKey} onChange={e => setNewDomainKey(e.target.value)} className="flex-1" />
                            <Button type="button" variant="outline" size="sm" onClick={addDomainKey}>Add</Button>
                        </div>
                    </Section>

                    <Section title="ZeptoMail — domain provisioning">
                        <Row label="Automatic setup">
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={zeptoAutoProvision}
                                    onChange={e => setZeptoAutoProvision(e.target.checked)}
                                />
                                Enable automatic domain configuration via API
                            </label>
                            <p className="mt-1 text-xs text-muted-foreground">
                                When enabled, new sending domains are created and verified through the ZeptoMail API.
                                When disabled, domains are managed manually in the ZeptoMail dashboard — only paste the Send Token below.
                            </p>
                        </Row>

                        {zeptoAutoProvision && (
                            <>
                                <Row label="Account API Key">
                                    <div className="flex gap-2">
                                        <Input
                                            type={showZeptoAccountKey ? "text" : "password"}
                                            value={zeptoAccountKey}
                                            onChange={e => setZeptoAccountKey(e.target.value)}
                                            placeholder={config?.zeptoAccountApiKeySet ? "•••• (set)" : "Account-level key for domain management"}
                                        />
                                        <button type="button" onClick={() => setShowZeptoAccountKey(v => !v)}
                                            className="p-2 text-muted-foreground hover:text-foreground">
                                            {showZeptoAccountKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        Required for API-based domain creation and verification. Separate from Send Tokens.
                                    </p>
                                </Row>

                                <div className="mt-4 space-y-2">
                                    <p className="text-sm font-medium">Add sending domain</p>
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="dairygiant.com"
                                            value={newZeptoDomain}
                                            onChange={e => setNewZeptoDomain(e.target.value)}
                                            onKeyDown={e => { if (e.key === "Enter") void createZeptoDomain(); }}
                                        />
                                        <Button
                                            type="button"
                                            disabled={creatingDomain || !newZeptoDomain.trim()}
                                            onClick={() => void createZeptoDomain()}
                                        >
                                            <Plus className="mr-1 h-4 w-4" />
                                            {creatingDomain ? "Creating…" : "Create"}
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}

                        {zeptoAutoProvision && zeptoDomains.length > 0 && (
                            <div className="mt-4 space-y-2">
                                {zeptoDomains.map(d => (
                                    <div key={d.domainKey} className="rounded-md border">
                                        <div className="flex items-center gap-3 px-4 py-3">
                                            <span className={`h-2 w-2 rounded-full ${
                                                d.status === "verified" ? "bg-green-500" : "bg-amber-400"
                                            }`} />
                                            <span className="font-mono text-sm">{d.domain}</span>
                                            <span className="text-xs text-muted-foreground">{d.status}</span>
                                            <div className="ml-auto flex gap-2">
                                                {d.dnsRecords.length > 0 && (
                                                    <button
                                                        type="button"
                                                        className="text-xs text-primary hover:underline"
                                                        onClick={() => setExpandedDomain(v => v === d.domainKey ? null : d.domainKey)}
                                                    >
                                                        {expandedDomain === d.domainKey ? "Hide DNS" : "DNS records"}
                                                    </button>
                                                )}
                                                {d.status !== "verified" && (
                                                    <button
                                                        type="button"
                                                        className="flex items-center gap-1 text-xs text-primary hover:underline"
                                                        onClick={() => void verifyZeptoDomain(d.domainKey)}
                                                    >
                                                        <RefreshCw className="h-3 w-3" /> Verify
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {expandedDomain === d.domainKey && d.dnsRecords.length > 0 && (
                                            <div className="border-t px-4 pb-4 pt-3">
                                                <p className="mb-2 text-xs text-muted-foreground">
                                                    Add these DNS records for <strong>{d.domain}</strong>, then click Verify.
                                                </p>
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-xs">
                                                        <thead>
                                                            <tr className="text-left text-muted-foreground">
                                                                <th className="pb-1 pr-4 font-medium">Type</th>
                                                                <th className="pb-1 pr-4 font-medium">Host</th>
                                                                <th className="pb-1 pr-4 font-medium">Value</th>
                                                                <th className="pb-1 font-medium">Purpose</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {d.dnsRecords.map((rec, i) => (
                                                                <tr key={i} className="border-t first:border-0">
                                                                    <td className="py-1 pr-4 font-mono">{rec.type}</td>
                                                                    <td className="py-1 pr-4 font-mono break-all">{rec.host}</td>
                                                                    <td className="py-1 pr-4 font-mono break-all max-w-[200px]">{rec.value}</td>
                                                                    <td className="py-1 text-muted-foreground">{rec.purpose}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>
                </>
            )}

            {isSendGrid && (
                <Section title="SendGrid settings">
                    <Row label="API Key">
                        <div className="flex gap-2">
                            <Input
                                type={showSendgridKey ? "text" : "password"}
                                value={sendgridKey}
                                onChange={e => setSendgridKey(e.target.value)}
                                placeholder={config?.sendGridApiKeySet ? "•••• (set — enter new to change)" : "SG.…"}
                            />
                            <button type="button" onClick={() => setShowSendgridKey(v => !v)}
                                className="p-2 text-muted-foreground hover:text-foreground">
                                {showSendgridKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </Row>
                </Section>
            )}

            <div className="flex gap-3 pt-2">
                <Button onClick={() => void save()} disabled={saving}>
                    {saving ? "Saving…" : "Save configuration"}
                </Button>
                <Button variant="outline" onClick={() => void loadConfig()}>Reset</Button>
            </div>
        </div>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="rounded-lg border p-5">
            <h2 className="mb-4 text-sm font-semibold text-muted-foreground uppercase tracking-wide">{title}</h2>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="grid grid-cols-[180px_1fr] items-start gap-4">
            <label className="pt-2 text-sm text-muted-foreground">{label}</label>
            <div>{children}</div>
        </div>
    );
}
